import React, { useState, useEffect, useRef } from 'react';
import { FiCode, FiSearch, FiX, FiCpu, FiUser } from 'react-icons/fi';
import { artifactoryService } from '../../services/artifactoryService';
import { Spinner } from '../common/Spinner';
import { useTheme } from '../../contexts/ThemeContext';

// Define persona interface
interface Persona {
  id: string;
  name: string;
  specialization: string;
  tags: string[];
  avatar?: string;
}

// Sample personas - in a real app, these would come from an API
const samplePersonas: Persona[] = [
  {
    id: 'System_Architect',
    name: 'System Architect',
    specialization: 'Full-stack architecture and system design',
    tags: ['Architecture', 'Full-Stack']
  },
  {
    id: 'React_Architect',
    name: 'React Architect',
    specialization: 'React component design and frontend architecture',
    tags: ['Frontend', 'React']
  },
  {
    id: 'Python_Backend',
    name: 'Python Backend Developer',
    specialization: 'Python API and service development',
    tags: ['Backend', 'Python']
  },
  {
    id: 'FullStack_Dev',
    name: 'Full Stack Developer',
    specialization: 'End-to-end application development',
    tags: ['Full-Stack', 'JavaScript', 'Python']
  },
  {
    id: 'UI_Designer',
    name: 'UI Designer',
    specialization: 'User interface and component design',
    tags: ['Frontend', 'UI/UX']
  }
];

export const ScaffoldingForm: React.FC<{ sessionId: string, onComplete: (manifestId: string) => void }> = ({ sessionId, onComplete }) => {
  const [intent, setIntent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { darkMode } = useTheme();

  // Filter personas based on search term
  const filteredPersonas = samplePersonas.filter(persona => 
    persona.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    persona.specialization.toLowerCase().includes(searchTerm.toLowerCase()) ||
    persona.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Enter to submit
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isLoading && intent.trim()) {
        e.preventDefault();
        handleSubmit(new Event('submit') as unknown as React.FormEvent);
      }
      
      // Escape to clear
      if (e.key === 'Escape') {
        setIntent('');
        setSearchTerm('');
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [intent, isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!intent.trim()) return;

    setIsLoading(true);
    try {
      const result = await artifactoryService.scaffoldProject(
        sessionId, 
        intent,
        undefined, // rootPath
        selectedPersona?.id // Pass selected persona ID if available
      );
      onComplete(result.manifest_id);
    } catch (error) {
      console.error('Error scaffolding project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setIntent('');
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const selectPersona = (persona: Persona | null) => {
    setSelectedPersona(persona);
    setIsDropdownOpen(false);
    setSearchTerm('');
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <div className="p-6 rounded-lg shadow-lg bg-gray-800 border-l-4 border-blue-700">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <div className="mr-3 p-2 rounded-lg bg-blue-900 bg-opacity-30">
          <FiCode className="h-5 w-5 text-blue-300" />
        </div>
        Create Project Structure
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block mb-2 font-medium text-gray-300">
            Describe your project:
          </label>
          <div className="relative">
            <textarea
              ref={textareaRef}
              className="w-full p-3 rounded-md bg-gray-700 text-white border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border transition-colors"
              rows={4}
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              placeholder="e.g., Create a React frontend with TypeScript and a FastAPI backend..."
              required
            />
            {intent && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute top-2 right-2 text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-600"
                aria-label="Clear text"
              >
                <FiX />
              </button>
            )}
          </div>
          <div className="mt-2 flex justify-between items-center">
            <p className="text-xs text-gray-400">
              Be specific about the project structure, technologies, and components you need.
            </p>
            <p className="text-xs text-gray-400">
              {intent.length} characters {intent.length > 0 && `(${Math.ceil(intent.length / 4)} tokens)`}
            </p>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            <span className="text-blue-400">Pro tip:</span> Press <kbd className="px-1 py-0.5 bg-gray-700 rounded">Ctrl</kbd>+<kbd className="px-1 py-0.5 bg-gray-700 rounded">Enter</kbd> to generate
          </p>
        </div>

        {/* Persona Selector */}
        <div className="mb-4">
          <label className="block mb-2 font-medium text-gray-300">
            Select Architect Persona:
          </label>
          <div className="relative" ref={dropdownRef}>
            <div 
              className="flex items-center p-3 rounded-md bg-gray-700 text-white border border-gray-600 cursor-pointer"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              {selectedPersona ? (
                <>
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center mr-2">
                    <FiUser className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{selectedPersona.name}</div>
                    <div className="text-xs text-gray-400">{selectedPersona.specialization}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center mr-2">
                    <FiCpu className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Auto-Select</div>
                    <div className="text-xs text-gray-400">Automatically choose the best persona</div>
                  </div>
                </>
              )}
            </div>
            
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-gray-800 rounded-md shadow-lg max-h-60 overflow-auto">
                {/* Search input */}
                <div className="sticky top-0 p-2 bg-gray-800 border-b border-gray-700">
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full p-2 pl-8 rounded-md bg-gray-700 text-white border-gray-600 focus:border-blue-500"
                      placeholder="Search personas..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                
                {/* Auto-select option */}
                <div 
                  className={`px-4 py-2 text-white cursor-pointer ${
                    !selectedPersona ? 'bg-green-800' : 'hover:bg-gray-700'
                  }`}
                  onClick={() => selectPersona(null)}
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center mr-2">
                      <FiCpu className="text-white" />
                    </div>
                    <div>
                      <div className="font-medium">Auto-Select</div>
                      <div className="text-xs text-gray-400">Automatically choose the best persona</div>
                    </div>
                  </div>
                </div>
                
                {/* Divider */}
                <div className="border-t border-gray-700 my-1"></div>
                
                {/* Filtered persona options */}
                {filteredPersonas.length > 0 ? (
                  filteredPersonas.map(persona => (
                    <div 
                      key={persona.id} 
                      className={`px-4 py-2 text-white cursor-pointer ${
                        selectedPersona?.id === persona.id ? 'bg-blue-800' : 'hover:bg-gray-700'
                      }`}
                      onClick={() => selectPersona(persona)}
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center mr-2">
                          <FiUser className="text-white" />
                        </div>
                        <div>
                          <div className="font-medium">{persona.name}</div>
                          <div className="text-xs text-gray-400">{persona.specialization}</div>
                        </div>
                        <div className="ml-auto flex space-x-1">
                          {persona.tags.map(tag => (
                            <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-gray-600">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-400 text-center">No matching personas</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 rounded-md bg-gray-600 hover:bg-gray-700 text-white shadow-md transition-colors"
            disabled={isLoading || !intent.trim()}
          >
            Clear
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-colors flex items-center justify-center"
            disabled={isLoading || !intent.trim()}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Generating...
              </>
            ) : (
              <>
                <FiCode className="mr-2" />
                Generate Project Structure
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
